import styled from 'styled-components'

import SvgIconPlusCircle from 'components/icons/PlusCircle'
import SvgIconMinusCircle from 'components/icons/MinusCircle'
import typeStyle from 'styles/typeStyle'

const OptionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 12px 0;

  /*
    Offset to the right to compensate for the size of the button, so the icon lines up with the grid on the right.
    The button needs a decent target area to be accessible, but the icon needs to visually align with the grid.
  */
  margin-right: -12px;

  &:first-child {
    margin-top: 0px;
  }
`

const OptionLabel = styled.div`
  ${typeStyle('-ds-font-lead-sm')}
  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
`

const OptionAnnotation = styled.span`
  ${typeStyle('-ds-font-body-sm-light')}
  display: block;
  color: ${({ theme }) => theme?.['-ds-neutral-500']?.value};
  margin-top: 4px;
`

const StepperWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
`

const StepButton = styled.button`
  width: 48px;
  height: 48px;
  padding: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  appearance: none;
  border: none;
  background: none;
  text-align: center;

  color: ${({ theme }) => theme?.['-ds-core-400']?.value};

  &:disabled {
    color: ${({ theme }) => theme?.['-ds-neutral-400']?.value};
  }

  svg {
    width: 22px;
    height: 22px;
    touch-action: none;
  }
`

const Value = styled.span`
  ${typeStyle('-ds-font-lead-sm')}
  flex: 1;
  min-width: 32px;

  color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
  text-align: center;
`

export const AGE_ANNOTATIONS = {
  ADULT: 'Ages 13 or above',
  CHILD: 'Ages 2 to 12',
} as const

type QuantitySelectProps = {
  label: string
  annotation?: string
  value?: number
  maxValue: number
  zeroDisplayValue?: string
  onChange: (value: number) => void
}

const QuantitySelect = ({
  label,
  annotation,
  value,
  maxValue,
  zeroDisplayValue = 'Any',
  onChange,
}: QuantitySelectProps) => {
  const quantity = Number(value || 0)

  let displayValue = quantity > 0 ? quantity.toString() : zeroDisplayValue
  if (quantity >= maxValue) displayValue = `${maxValue}+`

  const disableIncrement = quantity >= maxValue
  const disableDecrement = quantity <= 0

  return (
    <OptionWrapper data-testid={`${label.toLowerCase()}-quantity`}>
      <OptionLabel>
        {label}
        {annotation && <OptionAnnotation>{annotation}</OptionAnnotation>}
      </OptionLabel>

      <StepperWrapper>
        <StepButton
          aria-label="Decrease"
          onClick={() => onChange(Math.max(quantity - 1, 0))}
          disabled={disableDecrement}
        >
          <SvgIconMinusCircle />
        </StepButton>
        <Value>{displayValue}</Value>
        <StepButton
          aria-label="Increase"
          onClick={() => onChange(Math.min(quantity + 1, maxValue))}
          disabled={disableIncrement}
        >
          <SvgIconPlusCircle />
        </StepButton>
      </StepperWrapper>
    </OptionWrapper>
  )
}

export default QuantitySelect

type PredefinedQuantitySelectProps = Pick<
  QuantitySelectProps,
  'value' | 'onChange'
>

export const AdultQuantitySelect = ({
  value,
  onChange,
}: PredefinedQuantitySelectProps) => {
  return (
    <QuantitySelect
      label="Adults"
      annotation={AGE_ANNOTATIONS.ADULT}
      maxValue={20}
      value={value}
      onChange={onChange}
    />
  )
}

export const ChildQuantitySelect = ({
  value,
  onChange,
}: PredefinedQuantitySelectProps) => {
  return (
    <QuantitySelect
      label="Children"
      annotation={AGE_ANNOTATIONS.CHILD}
      maxValue={10}
      zeroDisplayValue="0"
      value={value}
      onChange={onChange}
    />
  )
}

export const PetQuantitySelect = ({
  value,
  onChange,
}: PredefinedQuantitySelectProps) => {
  return (
    <QuantitySelect
      label="Pets"
      maxValue={10}
      zeroDisplayValue="0"
      value={value}
      onChange={onChange}
    />
  )
}
